/**
 * Day.js global configuration
 *
 * This file configures dayjs to use Spanish locale by default
 * across the entire application.
 *
 * Day.js is a modern, lightweight (2KB) alternative to moment.js with
 * a similar API but better tree-shaking and ESM support.
 *
 * IMPORTANT: This configuration is imported in main.tsx before any dayjs usage.
 * It sets the global locale to Spanish ('es').
 *
 * To change the locale in the future, simply modify the locale string:
 * - 'es' for Spanish (España)
 * - 'en' for English
 * - 'fr' for French
 * - etc.
 *
 * See https://day.js.org/docs/en/i18n/i18n for more locale options
 */

import dayjs from 'dayjs';
import 'dayjs/locale/es';
import relativeTime from 'dayjs/plugin/relativeTime';
import duration from 'dayjs/plugin/duration';
import calendar from 'dayjs/plugin/calendar';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';

// Extend dayjs with necessary plugins
dayjs.extend(relativeTime);
dayjs.extend(duration);
dayjs.extend(calendar);
dayjs.extend(localizedFormat);
dayjs.extend(customParseFormat);

// Set default global locale to Spanish
dayjs.locale('es');

// Verify locale was set correctly (development only)
if (import.meta.env.DEV) {
  const currentLocale = dayjs.locale();
  if (currentLocale !== 'es') {
    console.info(`Day.js locale is "${currentLocale}", expected "es"`);
  } else {
    console.info('✅ Day.js locale configured as Spanish (es)');
  }
}

export default dayjs;
